import { useState } from "react";
import type { TemplateType } from "@/components/offer/offerDocumentTypes";
import { DemoOfferDecyzyjna } from "@/components/mvp/DemoOfferDecyzyjna";
import { DemoOfferPrzekonujaca } from "@/components/mvp/DemoOfferPrzekonujaca";
import { DemoOfferReprezentacyjna } from "@/components/mvp/DemoOfferReprezentacyjna";
import { OfferThumbnail } from "@/components/mvp/OfferThumbnail";
import { OFFER_DOCUMENT_MODE_OPTIONS } from "@/components/mvp/offerDocumentModeOptions";
import styles from "@/components/mvp/OfferDocumentModeCards.module.css";

export type OfferDocumentModeCardsProps = {
  value: TemplateType | null;
  onChange: (value: TemplateType) => void;
};

export function OfferDocumentModeCards({ value, onChange }: OfferDocumentModeCardsProps) {
  const [activeDemo, setActiveDemo] = useState<TemplateType | null>(null);

  return (
    <>
      <div className={styles.cards} role="radiogroup" aria-label="Tryb oferty">
        {OFFER_DOCUMENT_MODE_OPTIONS.map((card) => {
          const isActive = value === card.value;

          return (
            <div
              key={card.value}
              role="radio"
              aria-checked={isActive}
              data-selected={isActive}
              tabIndex={0}
              onClick={() => onChange(card.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onChange(card.value);
                }
              }}
              className={styles.card}
            >
              {isActive ? (
                <span className={styles.check} aria-hidden>
                  ✓
                </span>
              ) : null}

              <button
                type="button"
                className={styles.thumbnail}
                onClick={(event) => {
                  event.stopPropagation();
                  setActiveDemo(card.value);
                }}
                aria-label={`Otwórz podgląd formatu ${card.title}`}
              >
                <OfferThumbnail
                  templateType={card.value}
                  width={220}
                  height={170}
                />
              </button>

              <span>
                <h3 className={styles.title}>{card.title}</h3>
                <p className={styles.description}>{card.description}</p>
              </span>

              <span>
                <p className={styles.sectionTitle}>Co zawiera</p>
                <ul className={styles.list}>
                  {card.sections.map((item) => (
                    <li key={item} className={styles.item}>
                      <span className={styles.itemMark} aria-hidden>
                        ✓
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                  {card.excludedSections?.map((item) => (
                    <li key={item} className={styles.item}>
                      <span className={styles.itemMark} aria-hidden>
                        -
                      </span>
                      <span className={styles.excluded}>{item}</span>
                    </li>
                  ))}
                </ul>
              </span>

              <span className={styles.footer}>
                <button
                  type="button"
                  className={styles.previewAction}
                  onClick={(event) => {
                    event.stopPropagation();
                    setActiveDemo(card.value);
                  }}
                >
                  Podgląd →
                </button>
                <button
                  type="button"
                  className={styles.action}
                  onClick={(event) => {
                    event.stopPropagation();
                    onChange(card.value);
                  }}
                >
                  Wybierz →
                </button>
              </span>
            </div>
          );
        })}
      </div>

      {activeDemo === "quick" ? (
        <DemoOfferDecyzyjna onClose={() => setActiveDemo(null)} />
      ) : null}
      {activeDemo === "explained" ? (
        <DemoOfferPrzekonujaca onClose={() => setActiveDemo(null)} />
      ) : null}
      {activeDemo === "premium" ? (
        <DemoOfferReprezentacyjna onClose={() => setActiveDemo(null)} />
      ) : null}
    </>
  );
}
